import { useEffect, useState } from 'react';
import { TypographySectionLabel, TypographyText } from '@/components/base/Typography';
import { DiamondIcon } from '@/components/diamonds/DiamondIcon';
import { cn } from '@/utils';

const DEFAULT_SHAPES = [
  { name: 'frame-1', label: 'ROUND' },
  { name: 'frame-5', label: 'OVAL' },
  { name: 'frame-26', label: 'CUSHION' },
  { name: 'frame-19', label: 'EMERALD' },
  { name: 'frame-11', label: 'PRINCESS' },
  { name: 'frame-15', label: 'RADIANT' },
  { name: 'frame-3', label: 'PEAR' },
  { name: 'frame-14', label: 'MARQUISE' },
  { name: 'frame-8', label: 'ASSCHER' },
  { name: 'frame-25', label: 'HEART' },
];

const ShapeButton = ({
  shape,
  isSelected,
  onToggle,
  showTooltip = true,
  iconSize = 24,
  buttonSize = 'h-10 w-10',
}) => {
  // Format enum labels (e.g. ROUND → Round)
  const formattedLabel = shape.label.charAt(0) + shape.label.slice(1).toLowerCase();

  return (
    <button
      onClick={() => onToggle(shape.label)}
      className={cn(
        `group relative flex ${buttonSize} items-center justify-center rounded-full transition-all duration-200`,
        'focus-visible:ring-primary/20 hover:bg-gray-100 focus:outline-none focus-visible:ring-2',
        isSelected && 'bg-primary/5',
      )}
      title={formattedLabel}
      aria-label={`${isSelected ? 'Deselect' : 'Select'} ${formattedLabel} shape`}
    >
      <DiamondIcon
        name={shape.name}
        size={iconSize}
        className={cn('transition-colors duration-200', isSelected ? 'text-primary' : 'text-black')}
      />

      {isSelected && (
        <div className="border-primary absolute inset-0 rounded-full border-2" aria-hidden="true" />
      )}

      {showTooltip && (
        <div
          className={cn(
            'pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 transform',
            'bg-primary text-primary-foreground rounded px-2 py-1 text-xs opacity-0 transition-opacity',
            'z-10 group-hover:opacity-100',
          )}
        >
          {formattedLabel}
        </div>
      )}
    </button>
  );
};

export const ShapeFilter = ({
  onChange,
  initialSelected = [],
  shapes = DEFAULT_SHAPES,
  showLabelsOnHover = true,
  hideLabel = false,
  className,
  maxSelections,
  iconSize = 24,
  buttonSize = 'h-10 w-10',
  ...props
}) => {
  const [selectedShapes, setSelectedShapes] = useState(initialSelected);

  // Sync internal state with external changes to initialSelected
  useEffect(() => {
    setSelectedShapes(initialSelected);
  }, [initialSelected]);

  const toggleShape = (shapeLabel) => {
    let newSelected;

    if (selectedShapes.includes(shapeLabel)) {
      newSelected = selectedShapes.filter((s) => s !== shapeLabel);
    } else {
      if (maxSelections && selectedShapes.length >= maxSelections) {
        // Replace the first selected shape if at max
        newSelected = [...selectedShapes.slice(1), shapeLabel];
      } else {
        newSelected = [...selectedShapes, shapeLabel];
      }
    }

    setSelectedShapes(newSelected);
    onChange?.(newSelected);
  };

  const isSelected = (shapeLabel) => selectedShapes.includes(shapeLabel);

  return (
    <div className={cn('space-y-4', className)} {...props}>
      {!hideLabel && (
        <div className="flex items-center gap-2">
          <TypographySectionLabel>Diamond Shape</TypographySectionLabel>
          {selectedShapes.length > 0 && (
            <TypographyText>({selectedShapes.length} selected)</TypographyText>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {shapes.map((shape) => (
          <ShapeButton
            key={shape.label}
            shape={shape}
            isSelected={isSelected(shape.label)}
            onToggle={toggleShape}
            showTooltip={showLabelsOnHover}
            iconSize={iconSize}
            buttonSize={buttonSize}
          />
        ))}
      </div>

      {maxSelections && (
        <div className="text-muted-foreground text-xs">
          Maximum {maxSelections} shape{maxSelections !== 1 ? 's' : ''} can be selected
        </div>
      )}
    </div>
  );
};
